/**
 * What happened between somebody's first brief and their account.
 *
 * The funnel we care about is one person's path, described a flow, built it, sent it to their
 * phone, filled it in, signed up, and the last step happens in a different part of the app from
 * the first, possibly after a redirect through Google. So the few facts needed to describe that
 * path are kept in the browser: how many flows they built, and when they first asked for one.
 *
 * Counts and timestamps only. A brief is the visitor's own business detail and never goes in
 * here, nor into any analytics property.
 */
const KEY = 'flowgate.journey';

const read = () => {
  try {
    return JSON.parse(window.localStorage.getItem(KEY) || '{}') || {};
  } catch {
    // Private browsing, disabled storage, or something else's key: the journey is a nicety.
    return {};
  }
};

const write = (journey) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(journey));
  } catch {
    // Never break a page over analytics bookkeeping.
  }
};

/** The moment they first asked for a flow. Kept as the FIRST one, not the latest. */
export const markFirstBrief = () => {
  const journey = read();
  if (journey.firstBriefAt) return;
  write({ ...journey, firstBriefAt: Date.now() });
};

/** One more flow actually built (a generation that came back with a flow in it). */
export const markFlowBuilt = () => {
  const journey = read();
  write({ ...journey, flowsBuilt: (journey.flowsBuilt || 0) + 1 });
};

/** Remembers that the session in this browser has no account behind it. */
export const markAnonymous = () => {
  const journey = read();
  if (journey.anonymous) return;
  write({ ...journey, anonymous: true });
};

/** True when the person signing in now is the one who was here without an account. */
export const wasAnonymous = () => !!read().anonymous;

/** Analytics properties describing the path to this point. Never the content of anything. */
export const journeyProperties = () => {
  const journey = read();
  return {
    flowsBuilt: journey.flowsBuilt || 0,
    minutesSinceFirstBrief: journey.firstBriefAt
      ? Math.round((Date.now() - journey.firstBriefAt) / 60000)
      : null,
  };
};

/** The path ended (they signed up); what follows is an account holder's ordinary use. */
export const clearJourney = () => {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // As above.
  }
};
